import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle2, Plus, Trash2, Building2, Users, GitBranch, Brain, Clock, Rocket } from 'lucide-react';
import { vdCompanyApi, vdDepartmentApi, vdDirectoryApi, vdRoutingApi, vdAIConfigApi, vdHoursApi, vdOnboardingApi } from '../api';

const STEPS = [
  { label: 'Company', icon: Building2 },
  { label: 'Departments', icon: Building2 },
  { label: 'Directory', icon: Users },
  { label: 'Routing', icon: GitBranch },
  { label: 'AI Service', icon: Brain },
  { label: 'Hours', icon: Clock },
  { label: 'Activate', icon: Rocket },
];

export default function VDOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Shared state across steps
  const [company, setCompany] = useState({ companyName: '', businessPhone: '', businessType: '', greeting: 'Thank you for calling. Please listen to the following options.', timezone: 'America/Los_Angeles', fallbackNumber: '', afterHoursEnabled: false });
  const [depts, setDepts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [rules, setRules] = useState([]);
  const [aiConfig, setAIConfig] = useState({ enabled: false, systemPrompt: '', faqs: '', fallbackScript: 'Let me transfer you to a representative.', maxRetries: 3, callSummaryEnabled: true });
  const [hours, setHours] = useState({
    timezone: 'America/Los_Angeles',
    schedule: Object.fromEntries(['monday','tuesday','wednesday','thursday','friday'].map(d => [d, { enabled: true, open: '09:00', close: '17:00' }]).concat(['saturday','sunday'].map(d => [d, { enabled: false, open: '', close: '' }]))),
    afterHoursMessage: 'We are currently closed. Please call back during business hours.',
  });

  // Pre-populate company data from signup
  useState(() => {
    vdCompanyApi.get().then(c => {
      if (c) setCompany(prev => ({ ...prev, companyName: c.companyName || '', businessPhone: c.businessPhone || '' }));
    }).catch(() => {});
  });

  // Add dialogs
  const [deptDialog, setDeptDialog] = useState(false);
  const [empDialog, setEmpDialog] = useState(false);
  const [ruleDialog, setRuleDialog] = useState(false);
  const [tempItem, setTempItem] = useState({});

  const progress = ((step + 1) / STEPS.length) * 100;

  const next = () => { if (step < STEPS.length - 1) setStep(step + 1); };
  const back = () => { if (step > 0) setStep(step - 1); };

  const activate = async () => {
    setSaving(true);
    await vdCompanyApi.update({ ...company, status: 'active' });
    for (const d of depts) await vdDepartmentApi.create(d);
    for (const e of emps) await vdDirectoryApi.create(e);
    for (const r of rules) await vdRoutingApi.create(r);
    await vdAIConfigApi.update(aiConfig);
    await vdHoursApi.update(hours);
    await vdOnboardingApi.complete();
    toast.success('VoiceDesk activated!');
    setSaving(false);
    navigate('/voicedesk/dashboard');
  };

  // ── Step Renderers ─────────────────────────────────────────────────

  const renderCompany = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Company Name *</Label><Input value={company.companyName} onChange={e => setCompany(p => ({ ...p, companyName: e.target.value }))} className="bg-card/5 border-white/10 mt-1" /></div>
        <div><Label>Business Phone *</Label><Input value={company.businessPhone} onChange={e => setCompany(p => ({ ...p, businessPhone: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="+1 (555) 000-0000" /></div>
        <div><Label>Business Type</Label><Input value={company.businessType} onChange={e => setCompany(p => ({ ...p, businessType: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="Dental, Legal, HVAC..." /></div>
        <div><Label>Timezone</Label><Input value={company.timezone} onChange={e => setCompany(p => ({ ...p, timezone: e.target.value }))} className="bg-card/5 border-white/10 mt-1" /></div>
      </div>
      <div><Label>Fallback Number</Label><Input value={company.fallbackNumber} onChange={e => setCompany(p => ({ ...p, fallbackNumber: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="Main operator line" /></div>
      <div><Label>Company Greeting</Label><Textarea value={company.greeting} onChange={e => setCompany(p => ({ ...p, greeting: e.target.value }))} className="bg-card/5 border-white/10 mt-1" rows={3} /></div>
    </div>
  );

  const renderDepartments = () => (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Add departments callers can be routed to</p>
        <Button size="sm" onClick={() => { setTempItem({ name: '', extension: '', forwardNumber: '' }); setDeptDialog(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-black"><Plus className="w-3 h-3 mr-1" />Add</Button>
      </div>
      {depts.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No departments yet. Add at least one.</p>}
      {depts.map((d, i) => (
        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-card/5">
          <div><span className="font-medium text-sm">{d.name}</span><span className="text-xs text-muted-foreground ml-3">Ext {d.extension}</span></div>
          <Button variant="ghost" size="icon" onClick={() => setDepts(depts.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-400 h-7 w-7"><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
      <Dialog open={deptDialog} onOpenChange={setDeptDialog}>
        <DialogContent className="bg-[#1a1d2e] border-white/10 text-foreground">
          <DialogHeader><DialogTitle>Add Department</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={tempItem.name || ''} onChange={e => setTempItem(p => ({ ...p, name: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="Sales" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Extension</Label><Input value={tempItem.extension || ''} onChange={e => setTempItem(p => ({ ...p, extension: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="100" /></div>
              <div><Label>Forward #</Label><Input value={tempItem.forwardNumber || ''} onChange={e => setTempItem(p => ({ ...p, forwardNumber: e.target.value }))} className="bg-card/5 border-white/10 mt-1" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeptDialog(false)} className="border-white/10">Cancel</Button>
            <Button onClick={() => { if (tempItem.name) { setDepts([...depts, { ...tempItem, active: true }]); setDeptDialog(false); } }} className="bg-emerald-500 hover:bg-emerald-600 text-black">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  const renderDirectory = () => (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Add employees for extension dialing</p>
        <Button size="sm" onClick={() => { setTempItem({ fullName: '', extension: '', directPhone: '', jobTitle: '' }); setEmpDialog(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-black"><Plus className="w-3 h-3 mr-1" />Add</Button>
      </div>
      {emps.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No employees yet. You can add them later too.</p>}
      {emps.map((e, i) => (
        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-card/5">
          <div><span className="font-medium text-sm">{e.fullName}</span><span className="text-xs text-muted-foreground ml-3">Ext {e.extension} &middot; {e.jobTitle}</span></div>
          <Button variant="ghost" size="icon" onClick={() => setEmps(emps.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-400 h-7 w-7"><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
      <Dialog open={empDialog} onOpenChange={setEmpDialog}>
        <DialogContent className="bg-[#1a1d2e] border-white/10 text-foreground">
          <DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full Name</Label><Input value={tempItem.fullName || ''} onChange={e => setTempItem(p => ({ ...p, fullName: e.target.value }))} className="bg-card/5 border-white/10 mt-1" /></div>
              <div><Label>Job Title</Label><Input value={tempItem.jobTitle || ''} onChange={e => setTempItem(p => ({ ...p, jobTitle: e.target.value }))} className="bg-card/5 border-white/10 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Extension</Label><Input value={tempItem.extension || ''} onChange={e => setTempItem(p => ({ ...p, extension: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="101" /></div>
              <div><Label>Direct Phone</Label><Input value={tempItem.directPhone || ''} onChange={e => setTempItem(p => ({ ...p, directPhone: e.target.value }))} className="bg-card/5 border-white/10 mt-1" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmpDialog(false)} className="border-white/10">Cancel</Button>
            <Button onClick={() => { if (tempItem.fullName) { setEmps([...emps, { ...tempItem, active: true, forwardingEnabled: true }]); setEmpDialog(false); } }} className="bg-emerald-500 hover:bg-emerald-600 text-black">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  const renderRouting = () => (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Define how calls get routed</p>
        <Button size="sm" onClick={() => { setTempItem({ label: '', triggerType: 'keyword', triggerValue: '', destinationType: 'department', destinationValue: '', priority: rules.length + 1 }); setRuleDialog(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-black"><Plus className="w-3 h-3 mr-1" />Add</Button>
      </div>
      {rules.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No routing rules yet. You can set these up later.</p>}
      {rules.map((r, i) => (
        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-card/5">
          <div><span className="font-medium text-sm">{r.label}</span><span className="text-xs text-muted-foreground ml-3">{r.triggerType}: {r.triggerValue} → {r.destinationValue}</span></div>
          <Button variant="ghost" size="icon" onClick={() => setRules(rules.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-400 h-7 w-7"><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
      <Dialog open={ruleDialog} onOpenChange={setRuleDialog}>
        <DialogContent className="bg-[#1a1d2e] border-white/10 text-foreground">
          <DialogHeader><DialogTitle>Add Routing Rule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Label</Label><Input value={tempItem.label || ''} onChange={e => setTempItem(p => ({ ...p, label: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="Route to Sales" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Trigger Value</Label><Input value={tempItem.triggerValue || ''} onChange={e => setTempItem(p => ({ ...p, triggerValue: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="sales, 1, 100..." /></div>
              <div><Label>Destination</Label><Input value={tempItem.destinationValue || ''} onChange={e => setTempItem(p => ({ ...p, destinationValue: e.target.value }))} className="bg-card/5 border-white/10 mt-1" placeholder="Sales, ext 100..." /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleDialog(false)} className="border-white/10">Cancel</Button>
            <Button onClick={() => { if (tempItem.label) { setRules([...rules, { ...tempItem, active: true }]); setRuleDialog(false); } }} className="bg-emerald-500 hover:bg-emerald-600 text-black">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  const renderAI = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label>Enable AI Customer Service</Label>
          <p className="text-xs text-muted-foreground">AI will handle incoming calls and answer questions</p>
        </div>
        <Switch checked={aiConfig.enabled} onCheckedChange={v => setAIConfig(p => ({ ...p, enabled: v }))} />
      </div>
      {aiConfig.enabled && (
        <>
          <div><Label>AI Instructions</Label><Textarea value={aiConfig.systemPrompt} onChange={e => setAIConfig(p => ({ ...p, systemPrompt: e.target.value }))} className="bg-card/5 border-white/10 mt-1" rows={4} placeholder="You are a customer service agent..." /></div>
          <div><Label>FAQs</Label><Textarea value={aiConfig.faqs} onChange={e => setAIConfig(p => ({ ...p, faqs: e.target.value }))} className="bg-card/5 border-white/10 mt-1" rows={3} placeholder="Q: What are your hours?&#10;A: Mon-Fri 9-5" /></div>
        </>
      )}
    </div>
  );

  const renderHours = () => {
    const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
    const LABELS = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };
    const updateDay = (day, field, val) => setHours(p => ({ ...p, schedule: { ...p.schedule, [day]: { ...p.schedule[day], [field]: val } } }));
    return (
      <div className="space-y-3">
        {DAYS.map(day => (
          <div key={day} className="flex items-center gap-3">
            <span className="w-10 text-sm text-muted-foreground">{LABELS[day]}</span>
            <Switch checked={hours.schedule[day]?.enabled} onCheckedChange={v => updateDay(day, 'enabled', v)} />
            {hours.schedule[day]?.enabled ? (
              <>
                <Input type="time" value={hours.schedule[day]?.open || '09:00'} onChange={e => updateDay(day, 'open', e.target.value)} className="w-28 bg-card/5 border-white/10 text-sm" />
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="time" value={hours.schedule[day]?.close || '17:00'} onChange={e => updateDay(day, 'close', e.target.value)} className="w-28 bg-card/5 border-white/10 text-sm" />
              </>
            ) : <span className="text-xs text-muted-foreground">Closed</span>}
          </div>
        ))}
      </div>
    );
  };

  const renderReview = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="bg-card/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-muted-foreground mb-1">Company</div><div className="font-medium text-sm">{company.companyName || '—'}</div><div className="text-xs text-muted-foreground">{company.businessPhone}</div></CardContent></Card>
        <Card className="bg-card/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-muted-foreground mb-1">Departments</div><div className="font-medium text-sm">{depts.length} configured</div></CardContent></Card>
        <Card className="bg-card/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-muted-foreground mb-1">Employees</div><div className="font-medium text-sm">{emps.length} added</div></CardContent></Card>
        <Card className="bg-card/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-muted-foreground mb-1">Routing Rules</div><div className="font-medium text-sm">{rules.length} rules</div></CardContent></Card>
        <Card className="bg-card/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-muted-foreground mb-1">AI Service</div><div className="font-medium text-sm">{aiConfig.enabled ? 'Enabled' : 'Disabled'}</div></CardContent></Card>
        <Card className="bg-card/5 border-white/10"><CardContent className="p-4"><div className="text-xs text-muted-foreground mb-1">Hours</div><div className="font-medium text-sm">{Object.values(hours.schedule).filter(d => d.enabled).length} days active</div></CardContent></Card>
      </div>
    </div>
  );

  const stepRenderers = [renderCompany, renderDepartments, renderDirectory, renderRouting, renderAI, renderHours, renderReview];

  return (
    <div className="min-h-screen bg-[#0f1117] p-4 md:p-8" data-testid="vd-onboarding">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg overflow-hidden"><img src="/assets/voicedesk-logo.png" alt="VoiceDesk" className="w-full h-full object-cover" /></div>
          <span className="font-bold text-lg text-foreground">VoiceDesk Setup</span>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s, i) => (
              <div key={i} className={`flex items-center gap-1.5 text-xs ${i <= step ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                <span className="hidden md:inline">{s.label}</span>
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-1.5 bg-card/10" />
        </div>

        {/* Step Content */}
        <Card className="bg-[#141721] border-white/10 mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {(() => { const Icon = STEPS[step].icon; return <Icon className="w-4 h-4 text-emerald-400" />; })()}
              Step {step + 1}: {STEPS[step].label}
            </CardTitle>
          </CardHeader>
          <CardContent>{stepRenderers[step]()}</CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={back} disabled={step === 0} className="border-white/10">
            <ArrowLeft className="w-4 h-4 mr-1" />Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium">
              Next<ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={activate} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium">
              {saving ? 'Activating...' : 'Activate VoiceDesk'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
